/**
 * @file     roles.ts
 * @purpose  Role registry + capability map for the access-control system.
 *           Single source of truth; route code asks for capabilities, never
 *           compares role strings inline. No Postgres CHECK enforces these —
 *           validation is app-side (the deal-finder enum-registry pattern).
 */

export const ROLES = ['owner', 'partner_realtor', 'partner_realtor_alumni', 'partner_mortgage'] as const;
export type Role = (typeof ROLES)[number];

/** Active partners who can still receive new client assignments. */
export const PARTNER_ROLES: readonly Role[] = ['partner_realtor', 'partner_mortgage'];

/** Roles the owner may mint an invite for. Alumni is a wind-down state, never an invite. */
export const INVITABLE_ROLES: readonly Role[] = ['owner', 'partner_realtor', 'partner_mortgage'];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function isPartnerRole(value: unknown): value is Role {
  return isRole(value) && PARTNER_ROLES.includes(value);
}

export function isAssignablePartnerRole(value: unknown): value is Role {
  return value === 'partner_realtor' || value === 'partner_mortgage';
}

export function isInvitableRole(value: unknown): value is Role {
  return isRole(value) && (INVITABLE_ROLES as readonly string[]).includes(value);
}

/**
 * Capability → roles that hold it. Adding a role means extending these maps,
 * not adding a new branch in route code.
 */
export const CAPABILITIES = {
  admin_panel: ['owner'],
  manage_invites: ['owner'],
  manage_assignments: ['owner'],
  partner_portal: ['partner_realtor', 'partner_mortgage'],
  realtor_client_workspace: ['partner_realtor', 'partner_realtor_alumni'],
  view_preapproval: ['owner', 'partner_mortgage'],
} as const satisfies Record<string, readonly Role[]>;

export type Capability = keyof typeof CAPABILITIES;

export function roleHasCapability(role: Role, capability: Capability): boolean {
  return (CAPABILITIES[capability] as readonly Role[]).includes(role);
}

/**
 * Conversation-thread lane a partner role operates in. Derived server-side; the
 * client never passes a thread type. Keys are the partner roles.
 */
export const PARTNER_THREAD_TYPE: Record<string, string> = {
  partner_realtor: 'realtor',
  partner_realtor_alumni: 'realtor',
  partner_mortgage: 'mortgage_specialist',
};

/**
 * inquiry_messages.sender_role a partner writes as. Derived server-side; the
 * client never passes a sender role. Exported alongside PARTNER_THREAD_TYPE so
 * an /admin route acting for a scoped partner reads the same map the partner
 * routes do, rather than hardcoding 'agent'.
 */
export const PARTNER_SENDER_ROLE: Record<string, 'agent' | 'broker'> = {
  partner_realtor: 'agent',
  partner_realtor_alumni: 'agent',
  partner_mortgage: 'broker',
};

/**
 * Post-redemption landing path per role. Realtor partners are admins one level
 * below the owner and land in the same console; the mortgage partner keeps the
 * deliberately narrow portal.
 */
export function destinationForRole(role: Role): string {
  return role === 'partner_mortgage' ? '/partner' : '/admin';
}

/** Human-facing label for a role (English; UI strings still route via i18n). */
export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  partner_realtor: 'Realtor subscriber',
  partner_realtor_alumni: 'Offboarded realtor',
  partner_mortgage: 'Mortgage partner',
};
